import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import NProgress from 'nprogress';
// import omit from "omit.js"
// import PropTypes from 'prop-types';
import { prefix, WebUI, getNid } from "../../wui-core/src/index"
import { ProgressBarProps, PartialProgressBarProps } from './iProgress';

const ROUND_PRECISION = 1000;

// /**
//  * 自定义验证children是<ProgressBar>的实例.
//  * @props 实例后的propTypes
//  * @propName 本身propTypes的属性名 即 children
//  * @componentName 实例后的children名 期望是ProgressBar
//  */
// function onlyProgressBar(props, propName, componentName) {
//     const children = props[propName];
//     if (!children) {
//         return null;
//     }

//     let error = null;

//     React.Children.forEach(children, child => {
//         if (error) {
//             return;
//         }

//         if (child.type === ProgressBar) { // eslint-disable-line no-use-before-define
//             return;
//         }

//         const childIdentifier = React.isValidElement(child) ?
//             child.type.displayName || child.type.name || child.type :
//             child;
//         error = new Error(
//             `Children of ${componentName} can contain only ProgressBar ` +
// 			`components. Found ${childIdentifier}.`
//         );
//     });

//     return error;
// }

// const propTypes = {
//     /**
// 	 *  最小数值
// 	 */
//     min: PropTypes.number,
//     /**
// 	 *  有效数值
// 	 */
//     now: PropTypes.number,
//     /**
// 	 *  最大数值
// 	 */
//     max: PropTypes.number,
//     /**
// 	 *  文字描述标签
// 	 */
//     label: PropTypes.node,
//     /**
// 	 *  文字描述标签显示
// 	 */
//     srOnly: PropTypes.bool,
//     /**
// 	 *  条纹样式
// 	 */
//     striped: PropTypes.bool,
//     /**
// 	 *  激活状态
// 	 */
//     active: PropTypes.bool,
//     /**
// 	 *  大小
// 	 */
//     size: PropTypes.oneOf(['xs', 'sm']),
//     /**
// 	 *  labelPosition
// 	 */
//     labelPosition: PropTypes.oneOf(['right', 'left', 'center']),
//     /**
// 	 *  子组件 必须是ProgressBar
// 	 */
//     children: onlyProgressBar,
//     /**
// 	 *  子组件 必须是ProgressBar
// 	 */
//     style: PropTypes.object,

//     /**
// 	 * @private
// 	 */
//     isChild: PropTypes.bool,
// };

const defaultProps: ProgressBarProps = {
    min: 0,
    max: 100,
    active: false,
    isChild: false,
    srOnly: false,
    striped: false,
    labelPosition: 'center'
};

/**
 * 计算实际显示比例
 * @now 显示的总数值
 * @min 最小数值
 * @max 最大数值
 */
function getPercentage(now: number, min: number, max: number) {
    const percentage = (now - min) / (max - min) * 100;
    return Math.round(percentage * ROUND_PRECISION) / ROUND_PRECISION;
}

const ProgressBarComponent: React.FC<ProgressBarProps> = (props) => {
    const [state, setState] = useState({
        now: props.now,
        max: props.max,
        min: props.min,
        active: props.active
    });

    useEffect(() => {
        const updates: Partial<typeof state> = {};
        if ('now' in props && state.now !== props.now) updates.now = props.now;
        if ('max' in props && state.max !== props.max) updates.max = props.max;
        if ('min' in props && state.min !== props.min) updates.min = props.min;
        if ('active' in props && state.active !== props.active) updates.active = props.active;

        if (Object.keys(updates).length > 0) {
            setState(prev => ({ ...prev, ...updates }));
        }
    }, []);

    const renderProgressBar = ({
        min,
        now,
        max,
        label,
        srOnly,
        striped,
        active,
        colors,
        labelPosition,
        className,
        style,
        clsPrefix,
        ...others
    }: Omit<ProgressBarProps, 'isChild'>) => {
        const childClsPrefix = clsPrefix + '-bar';
        const classes = useMemo(() => ({
            active: active,
            [`${childClsPrefix}-striped`]: active || striped,
            [`${childClsPrefix}-${colors}`]: colors
        }), [active, striped, colors, childClsPrefix]);

        const classNames = classnames(childClsPrefix, classes);

        return (
            <div
                {...others}
                role="progressbar"
                className={classnames(className, classNames)}
                style={{ width: `${getPercentage(now as number, min, max)}%`, ...style }}
            >
                <span className={`${prefix}-progress-label`}>
                    {srOnly ? <span className="sr-only">{label}</span> : label}
                </span>
            </div>
        );
    };

    const { isChild, ...restProps } = props;

    if (isChild) {
        return renderProgressBar(restProps);
    }

    const {
        label,
        srOnly,
        striped,
        colors,
        className,
        style,
        min,
        max,
        now,
        active,
        size,
        labelPosition,
        children,
        clsPrefix,
        ...wrapperProps
    } = restProps;

    const adapterNid = getNid(props);

    return (
        <div
            {...wrapperProps}
            className={classnames(className, clsPrefix, size)}
            {...adapterNid}
        >
            {children ?
                React.Children.map(children as React.ReactElement[], child =>
                    React.cloneElement(child, { isChild: true })
                ) :
                renderProgressBar({
                    min,
                    now,
                    max,
                    label,
                    srOnly,
                    striped,
                    active,
                    colors,
                    labelPosition,
                    className,
                    style,
                    clsPrefix,
                    ...wrapperProps
                })
            }
        </div>
    );
};

const ProgressBar = WebUI({ name: "bar", defaultProps })(ProgressBarComponent);

// ProgressBar.propTypes = propTypes;
Object.assign(ProgressBar, NProgress);

export default ProgressBar as PartialProgressBarProps;
